import { queueMacrotask } from 'module/helpers/macrotask';
import { Editor, Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
  CustomSectionNodeType,
  CustomSectionType,
} from 'api/custom-section/types';
import { portalWithAppContext } from 'components/organisms/app-context/portal-with-context';
import {
  CommandPickerFloatingTriggerProps,
  CommandPickerWithTriggerOptions,
} from './types';
import { CommandPickerWithTriggerStorage } from './types';
import {
  CommandPickerTriggerRef,
  CommandsPickerTrigger,
} from './ui/command-picker';
import {
  CommandPickerFloatingTrigger,
  CommandPickerFloatingTriggerRef,
} from './ui/floating-trigger';
import { CommandPickerWithInput } from './ui/picker-with-input';

// TOOD: split this file content into multiple files
const CommandPickerWithTriggerName = 'commandPickerWithTrigger';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [CommandPickerWithTriggerName]: {
      addCommandTriggerPlaceholder: (positionToAdd: number) => ReturnType;
      removeCommandTriggerPlaceholder: (positionToRemove: number) => ReturnType;
    };
  }
}
const CommandPickerWithTriggerKey = new PluginKey(CommandPickerWithTriggerName);

export const CommandPickerWithTrigger = Extension.create<
  CommandPickerWithTriggerOptions,
  CommandPickerWithTriggerStorage
>({
  name: CommandPickerWithTriggerName,
  addOptions() {
    return {
      items: [],
    };
  },
  addStorage() {
    return {
      placeholdersAt: new WeakMap(),
      floatingTriggerProps: new WeakMap(),
    };
  },
  addCommands() {
    return {
      addCommandTriggerPlaceholder: (positionToAdd) => () => {
        this.storage.placeholdersAt.set(this.editor, [
          ...(this.storage.placeholdersAt.get(this.editor) ?? []),
          positionToAdd,
        ]);

        return true;
      },
      removeCommandTriggerPlaceholder: (positionToRemove) => () => {
        this.storage.placeholdersAt.set(
          this.editor,
          this.storage.placeholdersAt
            .get(this.editor)
            ?.filter((position) => position !== positionToRemove) ?? [],
        );

        return true;
      },
    };
  },
  addProseMirrorPlugins() {
    let mousemoveTimeout: NodeJS.Timeout | null = null;

    return [
      new Plugin({
        key: CommandPickerWithTriggerKey,
        state: {
          apply: (tr, oldState) => {
            const positions =
              this.storage.placeholdersAt.get(this.editor) || [];
            const triggerProps =
              this.storage.floatingTriggerProps.get(this.editor) || undefined;

            const isPositionsChanged =
              JSON.stringify(positions) !==
              JSON.stringify(oldState.placeholdersAt);

            if (!tr.docChanged && !isPositionsChanged) {
              return {
                ...oldState,
                floatingTriggerProps: triggerProps,
              };
            }
            const updatedPositions = tr.docChanged
              ? positions.map((pos) => tr.mapping.map(pos))
              : positions;
            const decorations: Decoration[] = [];

            updatedPositions.forEach((position) => {
              const portalContainer = document.createElement('div');

              decorations.push(
                Decoration.widget(
                  position,
                  () => {
                    const root = portalWithAppContext({
                      container: portalContainer,
                      children: (
                        <CommandPickerWithInput
                          position={position}
                          editor={this.editor}
                          items={this.options.items}
                          afterRemovePlaceholder={() => {
                            root.unmount();
                            portalContainer.remove();
                          }}
                        />
                      ),
                    });

                    return portalContainer;
                  },
                  {
                    stopEvent: () => {
                      return true;
                    },
                    destroy: () => {
                      portalContainer.remove();
                    },
                  },
                ),
              );
            });

            let triggerRef: CommandPickerTriggerRef | null = null;
            const portalContainer = document.createElement('div');

            portalWithAppContext({
              container: portalContainer,
              children: (
                <CommandsPickerTrigger
                  position={tr.doc.content.size}
                  editor={this.editor}
                  ref={(el) => {
                    triggerRef = el;
                  }}
                />
              ),
            });

            decorations.push(
              Decoration.widget(
                tr.doc.content.size,
                (view, getPos) => {
                  triggerRef?.updatePosition(getPos() ?? tr.doc.content.size);

                  return portalContainer;
                },
                {
                  stopEvent: () => {
                    return true;
                  },
                  destroy: () => {
                    portalContainer.remove();
                  },
                },
              ),
            );

            return {
              placeholdersAt: updatedPositions,
              decorationSet: DecorationSet.create(tr.doc, decorations),
              floatingTriggerProps: triggerProps,
            };
          },
          init: () => {
            return {
              placeholdersAt: [] as number[],
              decorationSet: DecorationSet.empty,
              floatingTriggerProps: undefined as
                | CommandPickerFloatingTriggerProps
                | undefined,
            };
          },
        },
        view: (view) => {
          let scrollableParent: HTMLElement | null = null;

          const NEAR_END_THRESHOLD_PX = 120;

          const updateTriggerFromScroll = () => {
            if (!scrollableParent) {
              return;
            }
            const { scrollTop, scrollHeight, clientHeight } = scrollableParent;
            const isNearEnd =
              scrollTop + clientHeight >= scrollHeight - NEAR_END_THRESHOLD_PX;

            let triggerProps: CommandPickerFloatingTriggerProps;

            if (isNearEnd) {
              const rect = scrollableParent.getBoundingClientRect();
              const endPos = this.editor.state.doc.content.size;

              triggerProps = {
                visible: true,
                position: {
                  left: rect.left + rect.width / 2 - 20,
                  top: rect.bottom - 50,
                },
                nodePositionInEditor: endPos,
              };
            } else {
              triggerProps = { visible: false };
            }

            this.storage.floatingTriggerProps = new WeakMap().set(
              this.editor,
              triggerProps,
            );
            const tr = view.state.tr;

            tr.setMeta(CommandPickerWithTriggerKey, { triggerProps });

            view.dispatch(tr);
          };

          queueMacrotask(() => {
            scrollableParent = findScrollableParent(view.dom.parentElement);

            if (scrollableParent) {
              scrollableParent.addEventListener(
                'scroll',
                updateTriggerFromScroll,
                { passive: true },
              );
              updateTriggerFromScroll();
            }
          });

          let triggerRef: CommandPickerFloatingTriggerRef | null = null;
          const triggerContainer = document.createElement('div');
          const floatingTrigger = portalWithAppContext({
            container: triggerContainer,
            children: (
              <CommandPickerFloatingTrigger
                floatingTriggerProps={
                  this.storage.floatingTriggerProps.get(this.editor) ?? {
                    visible: false,
                  }
                }
                editor={this.editor}
                ref={(el) => {
                  triggerRef = el;
                }}
              />
            ),
          });

          document.body.appendChild(triggerContainer);

          return {
            update: (view, prevState) => {
              const previousState =
                CommandPickerWithTriggerKey.getState(prevState);
              const currentState = CommandPickerWithTriggerKey.getState(
                view.state,
              );

              const triggerPropsChanged =
                JSON.stringify(previousState.floatingTriggerProps) !==
                JSON.stringify(currentState.floatingTriggerProps);

              if (triggerPropsChanged) {
                triggerRef?.update(currentState.floatingTriggerProps);
              }
            },
            destroy: () => {
              floatingTrigger.unmount();
              triggerContainer.remove();
              scrollableParent?.removeEventListener(
                'scroll',
                updateTriggerFromScroll,
              );
            },
          };
        },
        props: {
          decorations: (state) => {
            return CommandPickerWithTriggerKey.getState(state).decorationSet;
          },

          handleDOMEvents: {
            mouseleave: (view, event) => {
              if (event.relatedTarget) {
                const isMovedToFloatingTrigger = document
                  .querySelector('.command-picker-floating-trigger')
                  ?.contains(event.relatedTarget as Node);

                if (isMovedToFloatingTrigger) {
                  return;
                }
              }
              const triggerProps = {
                visible: false,
              };

              this.storage.floatingTriggerProps = new WeakMap().set(
                this.editor,
                triggerProps,
              );
              const tr = view.state.tr;

              tr.setMeta(CommandPickerWithTriggerKey, {
                triggerProps,
              });

              view.dispatch(tr);
            },
            mousemove: (view, event) => {
              if (mousemoveTimeout) {
                return false;
              }

              mousemoveTimeout = setTimeout(() => {
                mousemoveTimeout = null;
              }, 50);
              const firstLevelNode = findCustomSectionOrGroupAtCursorPosition(
                event.clientX,
                event.clientY,
                this.editor,
              );

              if (firstLevelNode) {
                const node = this.editor.view.nodeDOM(
                  firstLevelNode.nodeStart,
                ) as HTMLElement;

                if (node) {
                  const nodeRect = node.getBoundingClientRect();
                  const cursorMovedY = event.clientY - nodeRect.y;
                  const editorLastChild = this.editor.state.doc.lastChild;
                  const isCurrentNodeLastChild =
                    editorLastChild != null &&
                    firstLevelNode.node.eq(editorLastChild);
                  const showOnTop =
                    isCurrentNodeLastChild ||
                    (cursorMovedY > 0 && cursorMovedY / nodeRect.height < 0.6);
                  const triggerPosition = {
                    left: nodeRect.left,
                    top: showOnTop ? nodeRect.top : nodeRect.bottom + 30,
                  };
                  const triggerProps = {
                    visible: true,
                    position: triggerPosition,
                    nodePositionInEditor: showOnTop
                      ? firstLevelNode.nodeStart
                      : firstLevelNode.nodeEnd,
                  };

                  this.storage.floatingTriggerProps = new WeakMap().set(
                    this.editor,
                    triggerProps,
                  );
                  const tr = view.state.tr;

                  tr.setMeta(CommandPickerWithTriggerKey, {
                    triggerProps,
                  });

                  view.dispatch(tr);
                }
              } else {
                const scrollable = findScrollableParent(view.dom.parentElement);
                const NEAR_END_PX = 120;
                const isNearEnd =
                  scrollable &&
                  scrollable.scrollTop + scrollable.clientHeight >=
                    scrollable.scrollHeight - NEAR_END_PX;

                const triggerProps: CommandPickerFloatingTriggerProps =
                  isNearEnd && scrollable
                    ? (() => {
                        const rect = scrollable.getBoundingClientRect();

                        return {
                          visible: true,
                          position: {
                            left: rect.left + rect.width / 2 - 20,
                            top: rect.bottom - 50,
                          },
                          nodePositionInEditor:
                            this.editor.state.doc.content.size,
                        };
                      })()
                    : { visible: false };

                this.storage.floatingTriggerProps = new WeakMap().set(
                  this.editor,
                  triggerProps,
                );
                const tr = view.state.tr;

                tr.setMeta(CommandPickerWithTriggerKey, {
                  triggerProps,
                });

                view.dispatch(tr);
              }
            },
          },
        },
      }),
    ];
  },
});

const findCustomSectionOrGroupAtCursorPosition = (
  x: number,
  y: number,
  editor: Editor,
) => {
  let currentX = x;

  while (currentX < window.innerWidth && currentX > 0) {
    const elementsAtPoint = document.elementsFromPoint(currentX, y);
    const proseMirrorIndex = elementsAtPoint.findIndex((el) =>
      el.classList.contains('ProseMirror'),
    );
    const elementsBeforeProseMirror = elementsAtPoint.slice(
      0,
      proseMirrorIndex,
    );

    if (elementsBeforeProseMirror.length > 0) {
      const firstElement = elementsBeforeProseMirror[0];
      const elementPos = editor.view.posAtDOM(firstElement, 0);

      if (elementPos >= 0) {
        const $pos = editor.state.doc.resolve(elementPos);

        for (let depth = $pos.depth; depth >= 0; depth--) {
          const node = $pos.node(depth);

          if (
            node.type.name === CustomSectionNodeType.RlCustomSection ||
            node.type.name === CustomSectionNodeType.RlGroupSection
          ) {
            if (node.type.name === CustomSectionNodeType.RlCustomSection) {
              const hasGroupSectionAncestor = (() => {
                for (let d = depth - 1; d >= 0; d--) {
                  if (
                    $pos.node(d).type.name ===
                    CustomSectionNodeType.RlGroupSection
                  ) {
                    return true;
                  }
                }

                return false;
              })();
              const isQuestionAnswerItem =
                node.attrs?.sectionType ===
                CustomSectionType.QuestionAnswerItem;

              if (hasGroupSectionAncestor && isQuestionAnswerItem) {
                return undefined;
              }
            }

            return {
              node,
              nodeStart: $pos.start(depth) - 1,
              nodeEnd: $pos.end(depth) + 1,
              depth,
            };
          }
        }
      }
    }

    currentX += 1;
  }
};

const findScrollableParent = (element?: HTMLElement | null) => {
  if (!element || element === document.documentElement) {
    return null;
  }
  if (element.scrollHeight - 16 > element.clientHeight) {
    return element;
  }

  if (element.parentElement) {
    return findScrollableParent(element.parentElement);
  }

  return null;
};
